import { ColDef } from "ag-grid-community";
import { HourglassSplit } from "react-bootstrap-icons";
import { BaseReport } from "./BaseReport";
import { ReportConfig, FilterField } from "./types";
import { Api } from "../Data/Api";
import { cents, showMoney } from "../Data/Money";
import { ServerTransferSavedReportResponse } from "../shared/api/axios-client";
import { ParkerAgingRecord } from "../Data/ApiTypes";

export interface ColDefWithCurrency<T> extends ColDef<T> {
  isCurrency?: boolean;
}

export interface AgingFilter {
  page: number;
  pageSize: number;
  facilityId?: number;
  email?: string;
  name?: string;
  age?: "Any" | "30" | "60" | "90";
}
const highlight = (num: number, cls: string) =>
  num > 0 ? `text-end ${cls}` : "text-end";

const columns: ColDefWithCurrency<ParkerAgingRecord>[] = [
  { field: "name", headerName: "Name", valueGetter: (p) => p.data?.name },
  {
    field: "emailAddress",
    headerName: "Email",
    filter: false,
    valueGetter: (p) => p.data?.emailAddress,
  },
  {
    field: "balance30Days",
    headerName: "30 Days",
    type: "rightAligned",
    filter: false,
    valueFormatter: (p) => showMoney(cents(p.data!.balance30Days)),
    cellClass: (params) => highlight(params.value, "text-success"),
    isCurrency: true, 
  },
  {
    field: "balance60Days",
    headerName: "60 Days",
    type: "rightAligned",
    filter: false,
    valueFormatter: (params) => showMoney(cents(params.value)),
    cellClass: (params) => highlight(params.value, "text-warning"),
    isCurrency: true, 
  },
  {
    field: "balance90Days",
    headerName: "90 Days+",
    type: "rightAligned",
    filter: false,
    valueFormatter: (p) => showMoney(cents(p.data!.balance90Days)),
    cellClass: (params) => highlight(params.value, "text-danger"),
    isCurrency: true, 
  },
  {
    field: "totalBalance",
    headerName: "Total Balance",
    type: "rightAligned",
    filter: false,
    valueFormatter: (params) => showMoney(cents(params.value)),
    cellClass: (params) => highlight(params.value, "text-success fw-bold"),
    isCurrency: true, 
  },
];

const filterFields: FilterField[] = [
  {
    name: "age",
    label: "Age",
    type: "select",
    options: [
      { value: "30", label: "30 Days" },
      { value: "60", label: "60 Days" },
      { value: "90", label: "90 Days+" },
    ],
  },
  { name: "name", label: "Name", type: "text", placeholder: "Filter by name" },
  {
    name: "email",
    label: "Email",
    type: "text",
    placeholder: "Filter by email",
  },
];

const config: ReportConfig<ParkerAgingRecord, AgingFilter> = {
  id: "aging",
  title: "Aging Report",
  icon: (
    <HourglassSplit
      className="mx-1"
      style={{ fontSize: "1.5rem", marginTop: "-8px" }}
    />
  ),
  description: `
    <p>This report shows outstanding balances categorized by aging period.</p>
    <ul>
      <li>30 Days: balances outstanding 30–60 days</li>
      <li>60 Days: balances outstanding 60–90 days</li>
      <li>90+ Days: balances outstanding more than 90 days</li>
    </ul>
  `,
  defaultColumns: columns,
  filterFields,
  defaultFilterForm: {
    email: "",
    name: "",
    from: "",
    to: "",
    paymentType: "",
    paymentStatus: "",
    quickDateMode: "All",
    quickPayoutDateMode: "All",
    payoutFrom: "",
    payoutTo: "",
    age: "",
  },
  getData: async (filter) => {
    const response = await Api.agingReports(filter);
    if (!response.isOk) throw new Error(response.error);
    return response.value;
  },
};

export function AgingReport({
  facilityId,
  savedReports,
  setSavedReports,
}: {
  facilityId?: number;
  savedReports: ServerTransferSavedReportResponse[];
  setSavedReports: React.Dispatch<
    React.SetStateAction<ServerTransferSavedReportResponse[]>
  >;
}) {
  return (
    <BaseReport
      facilityId={facilityId}
      config={config}
      savedReports={savedReports}
      setSavedReports={setSavedReports}
      heightOffset={-90}
      storageKey='report_columns_aging'
    />
  );
}
